import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { primary, rs, useApi } from './ui';
import { ImportReportView } from '../../../components/admin/setup/ImportReport';

// Vendors, past bills and bill lines from one workbook. Checks first, imports on confirm. Never moves stock.
export const VendorImport = ({ onDone, compact }) => {
  const { call, download } = useApi();
  const [file, setFile] = useState(null);
  const [sheet, setSheet] = useState('vendors');
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState(null);
  const isCsv = file?.name?.toLowerCase().endsWith('.csv');
  const send = async (f, dry, sh = sheet) => {
    const fd = new FormData(); fd.append('file', f);
    const qs = new URLSearchParams({ dry_run: dry, sheet: f.name.toLowerCase().endsWith('.csv') ? sh : '' });
    return call('post', `/purchasing/import?${qs}`, fd);
  };
  const pick = async (f, sh = sheet) => {
    setFile(f); setCheck(null); setDone(null); if (!f) return;
    setBusy('check');
    try { setCheck(await send(f, true, sh)); } catch { setFile(null); } finally { setBusy(''); }
  };
  const run = async () => {
    setBusy('run');
    try {
      const r = await send(file, false);
      setDone(r); setCheck(null); setFile(null);
      toast.success(`Imported ${r.vendors} vendors and ${r.bills} bills`); onDone?.();
    } catch { /* toasted */ } finally { setBusy(''); }
  };
  const errs = (check?.errors || []).filter((e) => !e.skip || e.warn);
  const skips = (check?.errors || []).filter((e) => e.skip && !e.warn);
  return (
    <section className={`space-y-3 ${compact ? '' : 'rounded-xl border border-slate-200 bg-white p-5'}`} data-testid="vendor-import">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-slate-900">Vendors and past vendor bills</h2>
          <p className="text-xs text-slate-500">One Excel file with sheets <b>Vendors</b>, <b>Bills</b> and <b>Bill lines</b> (each optional). Bills set what you owe; stock is not changed. Bill lines feed price compare.</p>
        </div>
        <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400" onClick={() => download('/purchasing/import/template', 'vendor-import-template.xlsx')} data-testid="vendor-import-template"><Download size={13} /> Template</button>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700" data-testid="vendor-import-pick">
          {busy === 'check' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload Excel / CSV
          <input type="file" accept=".xlsx,.csv" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} data-testid="vendor-import-file" />
        </label>
      </div>
      {isCsv && (
        <label className="flex items-center gap-2 text-xs text-slate-600">This CSV has
          <select value={sheet} onChange={(e) => { setSheet(e.target.value); pick(file, e.target.value); }} className="rounded border px-2 py-1" data-testid="vendor-import-csv-kind">
            <option value="vendors">Vendors</option><option value="bills">Bills</option><option value="lines">Bill lines</option></select></label>)}
      {check && (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="vendor-import-check">
          <p className="text-sm">Ready: <b data-testid="vi-vendors">{check.vendors}</b> new vendors, <b data-testid="vi-bills">{check.bills}</b> bills{check.lines ? <>, <b>{check.lines}</b> bill lines</> : ''} · you will owe <b data-testid="vi-owed">{rs(check.owed)}</b></p>
          {!!skips.length && <p className="text-xs text-slate-500">{skips.length} already in Driveline, will be skipped.</p>}
          {!!errs.length && (
            <ul className="max-h-36 space-y-0.5 overflow-auto text-xs text-amber-800" data-testid="vendor-import-errors">
              {errs.slice(0, 50).map((e, i) => <li key={i} className="flex gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{e.sheet} row {e.row}: {e.msg}</li>)}
            </ul>)}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{errs.length ? 'Rows with problems are left out. Fix the file and upload again, or import the rest.' : 'Everything looks good.'}</span>
            <button type="button" className={`${primary} ml-auto`} disabled={!!busy || !(check.vendors || check.bills)} onClick={run} data-testid="vendor-import-run">
              {busy === 'run' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import</button>
          </div>
        </div>)}
      {done && (done.report ? <div data-testid="vendor-import-done"><ImportReportView id={done.report.id} /></div>
        : <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="vendor-import-done"><CheckCircle2 size={15} /> {done.vendors} vendors, {done.bills} bills imported · {rs(done.owed)} owed</p>)}
    </section>
  );
};
